import { Component, input, signal, inject, effect } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { TooltipModule } from 'primeng/tooltip';
import { DividerModule } from 'primeng/divider';
import { FieldsetModule } from 'primeng/fieldset';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { DadosUsuarioInput, UsuarioModel } from '@/core/models/usuario';
import { UsuarioService } from '../usuario.service';
import { UserService } from '@/core/services/user.service';
import { ToastModule } from 'primeng/toast';

@Component({
    selector: 'jornada-digital-usuario-data',
    templateUrl: './usuario-data.component.html',
    styleUrls: ['./usuario-data.component.scss'],
    standalone: true,
    imports: [
        ReactiveFormsModule,
        ButtonModule,
        InputTextModule,
        ProgressBarModule,
        TooltipModule,
        DividerModule,
        FieldsetModule,
        FloatLabelModule,
        IconFieldModule,
        InputIconModule,
        ToastModule
    ],
    providers: [MessageService]
})
export class UsuarioDataComponent {
  // Services
  private readonly usuarioService = inject(UsuarioService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly userService = inject(UserService);

  // Inputs
  usuario = input.required<UsuarioModel>();

  // Signals
  flagAdd = signal(false);
  flagDisabled = signal(false);
  flagLoad = signal(false);
  passwordType = signal<'password' | 'text'>('password');
  showPassword = signal(false);

  // Form
  usuarioForm!: FormGroup;

  constructor() {
    // Initialize flags
    this.flagDisabled.set(true);

    // Check permissions
    const hasPermission = this.userService.hasPermission('P_MAX') ||
                         this.userService.hasPermission('ACESSO_USUARIO_INS');
    this.flagAdd.set(hasPermission);

    // Initialize form
    this.usuarioForm = this.formBuilder.group({
      login: ['', [Validators.required, Validators.maxLength(100)]],
      nome: ['', [Validators.required, Validators.maxLength(500)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(500)]],
      senha: [''],
      confirmarSenha: [''],
      novaSenha: ['']
    }, { validators: this.passwordsMatchValidator });

    // Populate form when usuario changes
    effect(() => {
      const user = this.usuario();
      if (user) {
        this.usuarioForm.patchValue({
          login: user.login || '',
          nome: user.nome || '',
          email: user.email || ''
        });
      }
    });

    this.flagDisabled.set(false);

    console.log(this.usuario);
  }

  findUsuarioById() {
    const user = this.usuario();
    this.usuarioService.findUsuarioById(user.idUsuario!).subscribe((loadedUser) => {
      if (loadedUser) {
        this.usuarioForm.patchValue({
          login: loadedUser.login || '',
          nome: loadedUser.nome || '',
          email: loadedUser.email || ''
        });
      }
      this.flagDisabled.set(false);
      this.flagLoad.set(true);
    });
  }

  hideDialog(buscarDados: boolean): void {
    this.usuarioService.dialogUsuario.emit(buscarDados);
  }

  setUsuario(): void {
    if (this.usuarioForm.invalid) {
      return;
    }

    this.flagDisabled.set(true);
    const user = this.usuario();

    const payload: DadosUsuarioInput = {
      login: this.usuarioForm.get('login')?.value,
      email: this.usuarioForm.get('email')?.value,
      nome: this.usuarioForm.get('nome')?.value,
      senhaAntiga: this.usuarioForm.get('senha')?.value,
      novaSenha: this.usuarioForm.get('novaSenha')?.value,
      confirmarSenha: this.usuarioForm.get('confirmarSenha')?.value
    };

    this.usuarioService.setDadosUsuario(payload).subscribe({
      next: (result) => {
        if (result.statusRetorno === "OK") {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: result.mensagemRetorno,
            life: 3000
          });
          this.hideDialog(true);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: result.mensagemRetorno,
            life: 6000,
          });
          this.flagDisabled.set(false);
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao atualizar os dados do Usuário.',
          life: 3000,
        });
        this.flagDisabled.set(false);
      }
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword.update(show => !show);
    this.passwordType.set(this.showPassword() ? 'text' : 'password');
  }

  passwordsMatchValidator(form: FormGroup) {
    const novaSenha = form.get('novaSenha')?.value;
    const confirmarSenha = form.get('confirmarSenha')?.value;
    return novaSenha === confirmarSenha ? null : { senhasDiferentes: true };
  }
}
